<template>
	<div class="h-full" v-if="getAuthenticatedUserGroupLevel === '5' || getAuthenticatedUserGroupLevel === '6'">
		<BaseHeader :title="headerText" />
		<div v-if="!isSavedReturn">
			<div class="px-8 py-8 text-red-600" v-if="!isRepairProofFilled">
				{{ $t('save_repair_photos_before_car_return') }}
			</div>
			<div class="px-8 mt-12 text-left">
				<div>
					<div class="flex">
						<BaseDatepicker
							ref="Input.returndate"
							:required="true"
							:label="$t('return_date')"
							placeholder="01.01.2021"
							type="text"
							error-behavior="blur"
							v-model="returnData.returnDate"
							class="md:mr-4 w-full md:w-1/2"
							@input="getPDF"
							:max_date="new Date()"
						/>
						<BaseInput
							ref="Input.returnTime"
							:required="true"
							class="w-full md:w-1/2"
							:label="$t('return_time')"
							placeholder="17:00"
							type="tel"
							v-model="returnData.returnTime"
							maxlength="5"
							:mask="['NN:NN', 'N:NN']"
							validation="^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$"
							:error-message="$t('enter_valid_time')"
							@input="getPDF"
						/>
					</div>
				</div>

				<div class="flex space-x-4">
					<BaseSelect
						ref="Input.fueledByCustomer"
						:required="true"
						:label="$t('fuel_filled_by_customer')"
						:options="fueledByCustomer"
						v-model="returnData.isFueledByCustomerOption"
						class="w-1/2"
						@input="getPDF"
					/>
					<BaseInputNumber
						ref="Input.kilometrageAtStop"
						:required="true"
						:label="$t('mileage_return')"
						type="text"
						class="w-1/2"
						base-input-class="mb-0"
						v-model="returnData.kilometrageAtStop"
						:min-number="kilometrageAtStart"
						:error-message="$t('mileage_not_smaller_than', { startMileage: formatNumber(kilometrageAtStart) })"
						@input="getPDF"
					>
						<template #bottom>
							<div class="flex items-center space-x-4 ml-2">
								<font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="text-yellow-400 text-xl" @click="warnTachostand" />
								<div @click="warnTachostand">
									<div>{{ $t('rental_days', { days: getRentDaysCount }) }}</div>
									<div>{{ $t('mileage_rental_start_input', { mileage: formatNumber(kilometrageAtStart) }) }}</div>
								</div>
							</div>
						</template>
					</BaseInputNumber>
				</div>

				<div class="flex space-x-4 pt-8">
					<div class="w-1/2" style="font-weight: bold">{{ $t('damages_rental_start') }}</div>
					<div class="w-1/2" style="font-weight: bold">{{ $t('chose_extras') }}</div>
				</div>
				<div class="flex space-x-4">
					<div class="w-1/2" style="white-space: pre;">{{ rentalCarContract.damage !== '' ? rentalCarContract.damage : $t('none') }}</div>
					<div class="w-1/2">
						<div v-if="rentalCarContract.extras.length > 0">
							<div v-for="rentalCarContractExtra in rentalCarContract.extras" :key="rentalCarContractExtra.id">
								{{ rentalCarContractExtra.name }}
							</div>
						</div>
						<div v-else>
							{{ $t('none') }}
						</div>
					</div>
				</div>

				<div class="flex space-x-4 pt-8">
					<BaseSelect
						ref="Input.checkedForDamage"
						:required="true"
						:label="$t('looked_for_damages')"
						:options="checkedForDamage"
						v-model="returnData.isCheckedForDamageOption"
						class="w-1/2"
						@input="getPDF"
					/>

					<BaseSelect
						ref="Input.checkedForDamage"
						:required="true"
						:label="$t('found_new_damages')"
						:options="checkedForDamage"
						v-model="foundDamages"
						class="w-1/2"
						:disabled="!returnData.isCheckedForDamageOption || !returnData.isCheckedForDamageOption.value"
					/>
				</div>

				<div>
					<BaseTextarea
						ref="Input.listOfDefects"
						:label="$t('return_following_new_damages')"
						:required="true"
						:placeholder="$t('enter_new_damages')"
						v-model="returnData.listOfDefects"
						@focusout="getPDF"
						:error-message="$t('please_enter_new_damages')"
						:disabled="!foundDamages || !foundDamages.value"
					/>
				</div>
				<BaseSelect
					class="w-2/5 mt-6"
					ref="BaseSelect"
					required="true"
					:label="$t('client')"
					:options="clientOptions"
					v-model="selectedClient"
					:allow-empty="false"
					@input="emptyFields"
				/>
				<div v-if="selectedClient.value === 'other'">
					<CustomerForm
						ref="PersonNameForm"
						v-model="returnData.client"
						@updateValue="changedClientData"
						:show-contact-options="false"
						:disabled="false"
						:show-company="true"
					/>
				</div>

				<div class="pt-8 w-1/2 pr-6">
					<BaseInput
						ref="Input.signLocation"
						:required="true"
						class="w-full md:w-full"
						:label="$t('sign_location')"
						:placeholder="$t('place')"
						type="text"
						v-model="returnData.signLocation"
						@change="getPDF"
					/>
				</div>
				<div class="flex justify-between gap-12">
					<div class="w-full md:mb-8">
						<div class="text-center font-bold">{{ $t('sign_employee_input') }}</div>
						<BaseSign signature-pad="null" v-model="returnData.repairShopSign" @input="getPDF" />
					</div>
					<div class="w-full">
						<div class="text-center font-bold">{{ $t('sign_customer_input') }}</div>
						<BaseSign signature-pad="null" v-model="returnData.customerSign" @input="getPDF" />
					</div>
				</div>
				<div class="flex-grow flex justify-center mt-8" v-if="isPDFPreviewData">
					<div>
						<PDFThumbnailPreview
							:pdf-u-r-l="pdfURL"
							:uuid="uuid"
							@click="openPdf"
							show-route-params="true"
							type="rentalcar_contract_replacement_vehicle_return"
						/>
					</div>
				</div>
				<Footer @backButtonClick="$router.push({ name: 'rentalcarcontract.return' })">
					<template #continueButton>
						<SaveButton @click="save">{{ $t('save_return') }}</SaveButton>
					</template>
				</Footer>
			</div>
		</div>
	</div>
	<Forbidden v-else />
</template>

<script>
import { getYesNoOptions, getTimeFromDate, getUnixTimestamp } from '@/plugins/Utils';
import { PdfApiFactory, RentalCarContractApiFactory } from '@/api/generated';
import apiDefaults from '@/api/defaultConfiguration';
import BaseInput from '@/components/forms/inputs/BaseInput';
import BaseHeader from '@/components/navigation/BaseHeader';
import BaseInputNumber from '@/components/forms/inputs/BaseInputNumber';
import BaseSelect from '@/components/forms/inputs/BaseSelect';
import BaseDatepicker from '@/components/forms/inputs/BaseDatepicker';
import Validate from '@/mixins/Validate';
import BaseSign from '@/components/forms/inputs/BaseSign';
import Formatter from '@/mixins/Formatter';
import PDFThumbnailPreview from '@/components/damagecases/forms/PDFThumbnailPreview';
import BaseTextarea from '@/components/forms/inputs/BaseTextarea';
import Footer from '@/components/damagecases/Footer';
import SaveButton from '@/components/forms/buttons/SaveButton';
import Authentication from '@/mixins/Authentication';
import AppContainerMixin from '@/mixins/AppContainerMixin';
import Forbidden from '@/views/default/Forbidden.vue';
import CustomerForm from '@/components/forms/CustomerForm.vue';
import PDFDamagecasesRepository from '@/api/repositories/PDFDamagecasesRepository';

export default {
	name: 'RentalCarContractReturnForm',
	components: {
		CustomerForm,
		Forbidden,
		SaveButton,
		Footer,
		BaseTextarea,
		PDFThumbnailPreview,
		BaseSign,
		BaseDatepicker,
		BaseSelect,
		BaseInputNumber,
		BaseHeader,
		BaseInput,
	},
	mixins: [Validate, Formatter, Authentication, AppContainerMixin],
	data() {
		return {
			foundDamages: false,
			pdfURL: null,
			pdfBlob: null,
			documentName: '',
			savedReturn: false,
			headerText: this.$t('rental_car_return'),
			returnData: {
				customerSign: null,
				repairShopSign: null,
				returnDate: null,
				returnTime: null,
				kilometrageAtStop: null,
				isFueledByCustomerOption: null,
				isCheckedForDamageOption: null,
				listOfDefects: null,
				signLocation: null,
				client: {},
			},
			fueledByCustomer: getYesNoOptions(),
			checkedForDamage: getYesNoOptions(),
			kilometrageAtStart: 1,
			rentalCarContract: null,
			rentStartDate: null,
			clientOptions: [],
			selectedClient: null,
			uuid: '',
			originalSignLocation: '',
		};
	},
	created() {
		if (this.isUserAllowedInLevel5or6 && this.isAppContainer) {
			this.$store.commit('setConfirmLeaving', this.$t('leave_page'));
			this.getRentalCarContract();
			const currentDate = new Date();
			this.returnData.returnDate = currentDate;
			this.returnData.returnTime = getTimeFromDate(currentDate);
		}
	},
	computed: {
		getRentDaysCount() {
			const rentDate = new Date(this.rentStartDate * 1000);
			rentDate.setHours(0, 0, 0, 0);

			const now = new Date();
			now.setHours(23, 59, 59, 59);

			const diff = getUnixTimestamp(now) - getUnixTimestamp(rentDate);
			const daysCount = Math.floor(diff / 60 / 60 / 24) + 1;

			return daysCount;
		},
		getAPIData() {
			const time = this.returnData.returnTime.split(':');
			const hours = parseInt(time[0]);
			const minutes = parseInt(time[1]);

			if (!isNaN(hours) && !isNaN(minutes)) {
				this.returnData.returnDate.setHours(hours);
				this.returnData.returnDate.setMinutes(minutes);
			}

			return { ...this.returnData, rentEndDate: getUnixTimestamp(this.returnData.returnDate) };
		},
		isPDFPreviewData() {
			return (
				this.returnData.signLocation &&
				!isNaN(this.returnData.kilometrageAtStop) &&
				this.returnData.kilometrageAtStop &&
				this.returnData.returnDate &&
				this.returnData.returnTime &&
				this.returnData.isCheckedForDamageOption &&
				this.returnData.isFueledByCustomerOption
			);
		},
		isSavedReturn() {
			return this.savedReturn;
		},
		getAuthenticatedUserGroupLevel() {
			return this.$store.getters.getAuthenticatedUserGroupLevel;
		},
		isRepairProofFilled() {
			return this.checkRepairProofFilled();
		},
	},
	methods: {
		changedClientData(event) {
			this.returnData.client = event;
			if (
				this.returnData.client.firstName &&
				this.returnData.client.lastName &&
				this.returnData.client.street &&
				this.returnData.client.city.name &&
				this.returnData.client.city.zipCode
			) {
				this.getPDF();
			}
		},
		emptyFields() {
			if (this.selectedClient.value !== 'other') {
				this.returnData.client = {};
			}
		},
		warnTachostand() {
			this.$swal(this.$t('general.hint').toString(), this.$t('km_low_high_insurance_payback_warning').toString());
		},
		openPdf() {
			if (this.isAppContainer) {
				const reader = new FileReader();
				const name = this.documentName;
				reader.onload = function() {
					const arrayBuffer = this.result;
					// convert the ArrayBuffer to a comma-separated string
					const dataString = new Uint8Array(arrayBuffer).join(',');
					const messageData = {
						fileName: name,
						data: dataString,
					};
					// send the data to Swift
					window.webkit.messageHandlers.pdfHandler.postMessage(JSON.stringify(messageData));
				};
				reader.readAsArrayBuffer(this.pdfBlob);
			} else {
				const viewerUrl = `/blobviewer?blobUrl=${encodeURIComponent(this.pdfURL)}&title=${encodeURIComponent(this.documentName)}`;

				setTimeout(function() {
					window.open(viewerUrl, '_blank');
					window.URL.revokeObjectURL(viewerUrl);
				}, 200);
			}
		},
		openRepairProofImages() {
			this.$router.push({ name: 'damagecases.edit.repairProofs', params: { id: this.rentalCarContract.damageCase.id } });
		},
		checkRepairProofFilled() {
			const proofs = this.$store.getters.getRepairProof;
			return !(
				!proofs ||
				!proofs.frontViewImage ||
				!proofs.leftSideViewImage ||
				!proofs.rearViewImage ||
				!proofs.rightSideViewImage ||
				!proofs.speedometerImage
			);
		},
		getRentalCarContract() {
			RentalCarContractApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.rentalcarcontractIdGet(this.$route.params.contractId)
				.then(result => {
					this.rentalCarContract = result.data;
					this.returnData.id = this.rentalCarContract.id;
					if (result.data.uuid !== null) {
						new PDFDamagecasesRepository().getSavedDocument(result.data.uuid).then(res => {
							this.documentName = res.headers['content-disposition'].split('filename=')[1];
							const blob = new Blob([res.data], { type: 'application/pdf' });
							this.pdfURL = URL.createObjectURL(blob);
						});
					}
					if (result.data.rentEndDate !== null) {
						this.savedReturn = true;
					}
					this.headerText =
						this.$t('rental_car_return_from') +
						' ' +
						result.data.vehicle.licensePlate.text +
						' (' +
						result.data.vehicle.manufacturer +
						' ' +
						result.data.vehicle.model +
						')';

					this.rentStartDate = result.data.rentStartDate;

					if (result.data.sign_location) {
						this.returnData.signLocation = result.data.sign_location;
						this.originalSignLocation = result.data.sign_location;
					}

					const customer = this.rentalCarContract.damageCase.customer;
					const clientName = customer.gender === 'company' ? customer.companyName : customer.firstName + ' ' + customer.lastName;
					this.clientOptions.push({
						display: clientName,
						value: 'customer',
					});
					this.clientOptions.push({
						display: this.$t('other_client'),
						value: 'other',
						class: 'italic',
					});

					this.selectedClient = this.clientOptions[0];

					this.kilometrageAtStart = result.data.kilometrage;
					this.$store.dispatch('updateRepairProof', { id: this.rentalCarContract.damageCase.id });
				})
				.catch(error => {
					this.$swal(this.$t('general.error').toString(), this.$t('rental_car_contract_error').toString(), 'error');
				});
		},
		save() {
			if (this.validate()) {
				if (
					this.returnData.repairShopSign != null &&
					(this.returnData.customerSign != null || this.returnData.signLocation !== this.originalSignLocation)
				) {
					RentalCarContractApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
						.rentalcarcontractIdPut(this.rentalCarContract.id, this.getAPIData)
						.then(result => {
							this.$store.commit('setConfirmLeaving', false);
							this.savedReturn = true;
							this.pdfURL = apiDefaults.axios.defaults.baseURL + 'api/pdf/damagecases/' + result.data.uuid + '/file';
							this.$swal(this.$t('general.success').toString(), this.$t('return_saved').toString(), 'success');
							this.$router.push({ name: 'rentalcarcontract.return' });
						})
						.catch(error => {
							this.$swal(this.$t('general.error').toString(), this.$t('data_not_saved').toString(), 'error');
						});
				} else {
					this.$swal(this.$t('general.attention').toString(), this.$t('sign_process').toString(), 'warning');
				}
			} else {
				this.$swal(this.$t('general.attention').toString(), this.$t('please_check_input').toString(), 'warning');
			}
		},
		getPDF() {
			if (this.isPDFPreviewData) {
				this.pdfURL = '';
				PdfApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
					.pdfDamagecasesTypeTempSavePost('rentalcar_contract_replacement_vehicle_return', this.getAPIData)
					.then(res => {
						this.uuid = res.data.uuid;
						PdfApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
							.pdfDamagecasesUuidTempFileGet(this.uuid)
							.then(response => {
								const blob = new Blob([response.data], { type: 'application/pdf' });
								if (this.isAppContainer) {
									this.pdfBlob = blob;
								}
								this.documentName = response.headers['content-disposition'].split('filename=')[1];
								this.pdfURL = URL.createObjectURL(blob);
							});
					});
			}
		},
	},
};
</script>

<style scoped></style>
